import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import PropertyCard, { type PropertyCardData } from '../components/property/PropertyCard';

interface FavoriteItem {
  id: number;
  property_id: number;
  property: PropertyCardData;
}

interface FavoritesResponse {
  data: FavoriteItem[];
}

export default function FavoritesPage(): React.ReactElement {
  const { t } = useTranslation('catalog');
  const queryClient = useQueryClient();

  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((s) => ({ ...s, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Fetch favorites
  const { data: favoritesData, isLoading } = useQuery({
    queryKey: ['user-favorites-full'],
    queryFn: () => api.get<FavoritesResponse>('/v1/favorites'),
  });

  const favorites = favoritesData?.data ?? [];

  // Remove favorite mutation
  const removeMutation = useMutation({
    mutationFn: (propertyId: number) => api.delete(`/v1/favorites/${propertyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-favorites-full'] });
      queryClient.invalidateQueries({ queryKey: ['user-favorites'] });
      setToast({ show: true, message: t('favorites.removed'), type: 'success' });
    },
    onError: () => {
      setToast({ show: true, message: t('favorites.error'), type: 'error' });
    },
  });

  if (isLoading) {
    return (
      <div>
        <h1 className="text-3xl font-bold mb-6">{t('favorites.title')}</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i}>
              <div className="skeleton h-[200px] w-full rounded-lg" />
              <div className="skeleton h-8 w-full mt-2" />
              <div className="skeleton h-5 w-3/5 mt-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">{t('favorites.title')}</h1>

      {favorites.length === 0 ? (
        <div className="text-center py-16">
          <span className="text-6xl text-base-content/30 block mb-4">&#9825;</span>
          <h2 className="text-lg font-semibold text-base-content/60">
            {t('favorites.empty')}
          </h2>
          <p className="text-sm text-base-content/50">
            {t('favorites.emptyDescription')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {favorites.map((fav) => (
            <PropertyCard
              key={fav.id}
              property={fav.property}
              showFavorite
              isFavorite
              onToggleFavorite={(propertyId) => removeMutation.mutate(propertyId)}
            />
          ))}
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{toast.message}</span>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => setToast((s) => ({ ...s, show: false }))}
            >
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
